import { useCallback, useDeferredValue, useMemo } from 'react';

import { dashboardVesselApis } from '@apis/next/vesselApis';
import type { AutoCompleteConfig, AutoCompleteOption } from '@components/NextForms/TextInput';
import { useDebouncedValue } from '@hooks/useDebouncedValue';
import { Vessel } from '@models/next/Vessel';
import { MobxZodField, getDecodeResultOr } from '@monoid-dev/mobx-zod-form';
import { runInAction } from 'mobx';
import { pipe } from 'remeda';
import { ZodString } from 'zod';

export interface VesselAutoComplete {
  vessel: AutoCompleteConfig<Vessel>;
  callsign: AutoCompleteConfig<Vessel>;
}

const filterVessel: AutoCompleteConfig<Vessel>['filter'] = (value, options) => {
  return options
    .filter((o) => o.value.vessel.toLocaleUpperCase().includes(String(value).toLocaleUpperCase()))
    .slice(0, 100);
};

const filterCallsign: AutoCompleteConfig<Vessel>['filter'] = (value, options) => {
  return options
    .filter((o) => o.value.callsign.toLocaleUpperCase().includes(String(value).toLocaleUpperCase()))
    .slice(0, 100);
};

export const useVesselAutoComplete = (
  vesselField: MobxZodField<ZodString>,
  callsignField: MobxZodField<ZodString>,
  enabled: boolean = true,
): VesselAutoComplete => {
  const vessel = pipe(getDecodeResultOr(vesselField.decodeResult, ''), (v) =>
    useDebouncedValue(() => v, 100, [v]),
  );

  const callsign = pipe(getDecodeResultOr(callsignField.decodeResult, ''), (v) =>
    useDebouncedValue(() => v, 100, [v]),
  );

  const vesselByVesselQuery = dashboardVesselApis.useGet(
    '/dashboard/vessel',
    {
      queries: {
        vessel,
      },
    },
    {
      enabled: enabled && !!vessel,
      keepPreviousData: true,
    },
  );

  const vesselByCallsignQuery = dashboardVesselApis.useGet(
    '/dashboard/vessel',
    {
      queries: {
        callsign,
      },
    },
    {
      enabled: enabled && !!callsign,
      keepPreviousData: true,
    },
  );

  const vesselOptions = useDeferredValue(
    useMemo(() => {
      return (vesselByVesselQuery.data ?? []).map((vessel) => ({
        label: `${vessel.vessel} / ${vessel.callsign}`,
        value: vessel,
      }));
    }, [vesselByVesselQuery.data]),
  );

  const callsignOptions = useDeferredValue(
    useMemo(() => {
      return (vesselByCallsignQuery.data ?? []).map((vessel) => ({
        label: `${vessel.callsign} / ${vessel.vessel}`,
        value: vessel,
      }));
    }, [vesselByCallsignQuery.data]),
  );

  const onSelect = useCallback(
    (option: AutoCompleteOption<Vessel>) => {
      runInAction(() => {
        vesselField.setOutput(option.value.vessel);
        callsignField.setOutput(option.value.callsign);
      });
    },
    [vesselField, callsignField],
  );

  return {
    vessel: {
      autoCompleteOptions: vesselOptions,
      filter: filterVessel,
      onSelect,
    },
    callsign: {
      autoCompleteOptions: callsignOptions,
      filter: filterCallsign,
      onSelect,
    },
  };
};
